import React from 'react';
import { GlassCard } from './GlassCard';
import { Check, Star } from 'lucide-react';

interface PackageCardProps {
  name: string;
  price: string;
  period?: string;
  description?: string;
  features: string[];
  isPopular?: boolean;
  ctaText?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  variant?: 'default' | 'compact';
  className?: string;
}

export const PackageCard: React.FC<PackageCardProps> = ({
  name,
  price,
  period = '/mies.',
  description,
  features,
  isPopular = false,
  ctaText = 'Wybierz',
  ctaHref,
  onCtaClick,
  variant = 'default',
  className = '',
}) => {
  const CtaElement = ctaHref ? 'a' : 'button';
  const ctaProps = ctaHref
    ? { href: ctaHref, target: '_blank', rel: 'noopener noreferrer' }
    : { onClick: onCtaClick };

  if (variant === 'compact') {
    return (
      <GlassCard
        variant={isPopular ? 'strong' : 'medium'}
        hover
        className={`relative ${isPopular ? 'border-2 border-primary-light/50 dark:border-primary-dark/50' : ''} ${className}`}
      >
        {isPopular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Star className="w-3 h-3" />
            Popularne
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
            {name}
          </h3>
          <div className="text-right">
            <span className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {price}
            </span>
            {period && (
              <span className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                {period}
              </span>
            )}
          </div>
        </div>

        <ul className="space-y-2">
          {features.slice(0, 4).map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </GlassCard>
    );
  }

  // Default variant
  return (
    <GlassCard
      variant={isPopular ? 'strong' : 'medium'}
      hover
      className={`relative flex flex-col h-full ${isPopular ? 'border-2 border-primary-light/50 dark:border-primary-dark/50 scale-105' : ''} ${className}`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white text-sm font-semibold rounded-full flex items-center gap-1.5 shadow-lg">
          <Star className="w-4 h-4" />
          Najpopularniejszy
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
          {name}
        </h3>
        {description && (
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
            {description}
          </p>
        )}
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            {price}
          </span>
          {period && (
            <span className="text-text-secondary-light dark:text-text-secondary-dark">
              {period}
            </span>
          )}
        </div>
      </div>

      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3 text-text-secondary-light dark:text-text-secondary-dark">
            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-green-500" />
            </div>
            {feature}
          </li>
        ))}
      </ul>

      <CtaElement
        {...ctaProps}
        className={`
          w-full py-3 px-6 rounded-xl font-semibold text-center
          transition-all duration-300
          ${isPopular
            ? 'bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02]'
            : 'bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 text-text-primary-light dark:text-text-primary-dark hover:bg-white/20 dark:hover:bg-white/10'
          }
        `}
      >
        {ctaText}
      </CtaElement>
    </GlassCard>
  );
};

export default PackageCard;
